<script setup lang="ts">
import './assets/object-type-card.scss'
import { computed, ref, type PropType } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@/entities/Auth'
import type { ObjectTypeTypeShort } from '@/entities/Manuals/ObjectTypes'
import { VIcons } from '@/shared/ui'
import { pluralizeCount } from '@/shared/lib'
import ObjectTypeChip from './ObjectTypeChip.vue'
import ObjectTypeGroupRowActions from './ObjectTypeGroupRowActions.vue'
import ObjectTypeGroupOverlay from './ObjectTypeGroupOverlay.vue'

const props = defineProps({
  type: {
    type: Object as PropType<ObjectTypeTypeShort>,
    required: true,
  },
  objectId: {
    type: String,
    required: true,
  },
  section: {
    type: String,
    required: true,
  },
})
const emits = defineEmits(['changed'])

const route = useRoute()
const router = useRouter()
const auth = useAuth()

const overlayOpened = ref(false)

const canUpdate = computed(() => auth.hasTypePermission(Number(props.type.id), 'update'))

/** Ссылка на редактирование родительского инвентаризационного типа */
const parentEditLink = computed(() =>
  router.resolve({
    name: 'objects.groups.admin',
    params: {
      ...route.params,
      action: 'edit',
      entity_id: props.type.parent_id,
    },
  }),
)

/* Текст с количеством элементов в нужной плюральной форме */
const elementsText = computed(() =>
  pluralizeCount('itf.card_elements_count', props.type.elements_count ?? 0),
)
</script>

<template>
  <div class="object-type-card">
    <div class="object-type-card__header">
      <span class="object-type-card__parent">
        <span class="object-type-card__id">{{ type.parent_id }}</span>
        <span class="object-type-card__parent-name">{{ type.parent_name }}</span>
      </span>

      <ObjectTypeChip :type="type" />

      <div class="object-type-card__actions">
        <VTooltip v-if="canUpdate" location="top" :open-delay="500" :text="$t('itf.view')">
          <template #activator="{ props: tipProps }">
            <RouterLink
              v-bind="tipProps"
              :to="parentEditLink"
              class="object-type-card__icon-link"
            >
              <VIcons type="visibility" />
            </RouterLink>
          </template>
        </VTooltip>
      </div>
    </div>

    <div class="object-type-card__body">
      <div v-if="type.elements_count === 1" class="object-type-card__row">
        <span class="object-type-card__id">{{ type.first_element_id }}</span>
        <span class="object-type-card__name">{{ type.first_element_name }}</span>

        <ObjectTypeGroupRowActions
          :element-id="Number(type.first_element_id)"
          :type-id="Number(type.id)"
          :object-id="objectId"
          :section="section"
          @changed="emits('changed')"
        />
      </div>

      <div v-else class="object-type-card__row">
        <VTooltip location="top" :open-delay="500" :text="$t('itf.view')">
          <template #activator="{ props: tipProps }">
            <a
              v-bind="tipProps"
              class="object-type-card__icon-link"
              @click.prevent="overlayOpened = true"
            >
              <VIcons type="view_list" />
              <span class="object-type-card__count">{{ elementsText }}...</span>
            </a>
          </template>
        </VTooltip>
      </div>
    </div>

    <ObjectTypeGroupOverlay
      v-model="overlayOpened"
      :type="type"
      :object-id="objectId"
      :section="section"
      @changed="emits('changed')"
    />
  </div>
</template>
